/**
 * Cas de démonstration, dérivés des jeux de données de `front-dolibarr-agent`.
 *
 * Ce fichier est la **source unique** des cas : il est chargé à la fois par le
 * bouchon de `mock/serveur.mjs` (développement) et par le mode démonstration du
 * plugin déployé (`src/demo/index.ts`). Écrit en JavaScript pour rester
 * importable par Node comme par Vite ; sa surface est typée dans `cas.d.mts`.
 *
 * Les fixtures elles-mêmes ne sont jamais modifiées : chaque cas limite est
 * dérivé par copie.
 */

import resolu from './exemples/conversation-contexte-resolu.json' with { type: 'json' };
import inconnu from './exemples/conversation-contexte-inconnu.json' with { type: 'json' };
import poulaillon from './exemples/client-poulaillon.json' with { type: 'json' };
import todoBenjamin from './exemples/todo-benjamin.json' with { type: 'json' };
import relancesBenjamin from './exemples/relances-benjamin.json' with { type: 'json' };
import santeExemple from './exemples/sante.json' with { type: 'json' };
import erreur409 from './exemples/erreur-409.json' with { type: 'json' };

const copie = (valeur) => structuredClone(valeur);

/** Erreur du contrat, à traduire par l'appelant en réponse HTTP ou en rejet. */
const erreur = (statut, corps) => ({ erreur: { statut, corps } });

export const ERREUR_409 = () => erreur(409, copie(erreur409));

export const ERREUR_404 = () =>
  erreur(404, {
    erreur: 'conversation_inconnue',
    message: "Cette conversation n'est pas dans le référentiel.",
    indice: 'Le crawl ne couvre pas la boîte « Confidentiel ».',
  });

/* ------------------------------------------------- /conversation/{id} */

/**
 * Identifiants de démonstration. Les deux premiers sont les fixtures telles
 * quelles ; les suivants dérivent les cas limites que l'interface doit tenir.
 */
export const CONVERSATIONS = {
  cnv_1bktghfy: () => copie(resolu),
  cnv_1cp6xh1q: () => copie(inconnu),

  // Bilan périmé : de l'activité est postérieure à la synthèse.
  cnv_perime: () => {
    const contexte = copie(resolu);
    contexte.conversation.id = 'cnv_perime';
    contexte.fraicheur.genere_le = '2026-05-14T19:40:00Z';
    contexte.fraicheur.derniere_activite = '2026-08-22';
    contexte.fraicheur.perime = true;
    contexte.fraicheur.motif_peremption = '3 nouveaux messages depuis la synthèse';
    return contexte;
  },

  // Rattachement probable : le bilan s'affiche, avec sa réserve.
  cnv_probable: () => {
    const contexte = copie(resolu);
    contexte.conversation.id = 'cnv_probable';
    contexte.rattachement.statut = 'probable';
    contexte.rattachement.methode = 'domaine';
    contexte.rattachement.confiance = 0.62;
    contexte.rattachement.projet_ref = null;
    return contexte;
  },

  // Groupe dont la fiche complète est disponible : déplie /client/{groupe_id}.
  cnv_poulaillon: () => {
    const contexte = copie(resolu);
    const fiche = copie(poulaillon);
    contexte.conversation.id = 'cnv_poulaillon';
    contexte.conversation.sujet = 'Re: Mise en carton de manalas';
    contexte.conversation.interlocuteur = 'n.muszynski@poulaillon.fr';
    contexte.rattachement.groupe_id = fiche.groupe_id;
    contexte.rattachement.tiers_id = fiche.entites[0].tiers_id;
    contexte.rattachement.projet_ref = null;
    contexte.rattachement.methode = 'contact_front';
    contexte.client = {
      groupe: fiche.nom,
      site: fiche.entites[0].ville,
      canal: 'direct',
      integrateur: null,
      chiffre_ht: fiche.chiffres.chiffre_ht,
      commande_ht: fiche.chiffres.commande_ht,
      facture_ht: fiche.chiffres.facture_ht,
      nb_projets: fiche.chiffres.nb_projets,
      nb_projets_ouverts: fiche.chiffres.nb_projets_ouverts,
    };
    contexte.bilan = fiche.bilan;
    contexte.score_reprise = fiche.score_reprise;
    contexte.action_recommandee = fiche.affaires[0].action;
    contexte.signaux = [
      { niveau: 'critique', texte: 'Tensions de règlement récurrentes en 2025-2026' },
      { niveau: 'attention', texte: 'Flux SAV important, totalement hors contrat' },
    ];
    return contexte;
  },

  // Client identifié, bilan pas encore produit par le pipeline.
  cnv_409: () => ERREUR_409(),
};

export function contexteConversation(cnvId) {
  const cas = CONVERSATIONS[cnvId];
  return cas ? cas() : ERREUR_404();
}

/* ------------------------------------------------------ /client/{id} */

export const CLIENTS = {
  grp_poulaillon: () => copie(poulaillon),
  grp_leygatech: () => ERREUR_409(),
};

export function ficheClient(groupeId) {
  const cas = CLIENTS[groupeId];
  if (cas) return cas();
  return erreur(404, {
    erreur: 'client_inconnu',
    message: `Aucun groupe « ${groupeId} » dans le référentiel.`,
    indice: 'Identifiants de démonstration : grp_poulaillon, grp_leygatech.',
  });
}

/* ------------------------------------------- /todo et /relances */

/** Rejouée quel que soit l'identifiant, pour que le vrai `teammate_id` réponde. */
export function todo(teammateId) {
  const donnees = copie(todoBenjamin);
  donnees.teammate_id = teammateId;
  return donnees;
}

export function relances(teammateId, maintenant = Date.now()) {
  const donnees = copie(relancesBenjamin);
  donnees.teammate_id = teammateId;
  // L'échéance d'annulation est glissée dans le futur : figée, le compte à
  // rebours du plugin serait toujours dépassé.
  const brouillon = donnees.elements.find((element) => element.etat === 'brouillon');
  if (brouillon) {
    brouillon.envoi_prevu_le = new Date(maintenant + 45 * 60_000).toISOString();
    brouillon.annulable_jusqua = new Date(maintenant + 30 * 60_000).toISOString();
  }
  return donnees;
}

export function sante() {
  return copie(santeExemple);
}

/* ------------------------------------------------------- /recherche */

/**
 * Corpus de démonstration : tout le texte que les jeux de données contiennent
 * réellement. Rien n'est inventé — les extraits proviennent des bilans, des
 * signaux et des affaires des fixtures.
 */
function corpus() {
  const documents = [];

  // Seulement les conversations distinctes : `cnv_perime` et `cnv_probable`
  // rhabillent la même affaire Leygatech, les indexer produirait trois fois le
  // même résultat pour une seule conversation réelle.
  const distinctes = ['cnv_1bktghfy', 'cnv_1cp6xh1q', 'cnv_poulaillon'];

  for (const cnvId of distinctes) {
    const contexte = CONVERSATIONS[cnvId]();
    if (contexte.erreur) continue;
    documents.push({
      cnv_id: contexte.conversation.id ?? cnvId,
      sujet: contexte.conversation.sujet,
      client: contexte.client?.groupe ?? null,
      date: contexte.fraicheur.derniere_activite,
      corps: [
        contexte.bilan ?? '',
        contexte.action_recommandee ?? '',
        ...contexte.signaux.map((signal) => signal.texte),
      ]
        .filter(Boolean)
        .join(' '),
    });
  }

  const fiche = copie(poulaillon);
  for (const affaire of fiche.affaires) {
    documents.push({
      cnv_id: 'cnv_poulaillon',
      sujet: affaire.titre,
      client: fiche.nom,
      date: fiche.fraicheur.derniere_activite,
      corps: `${affaire.statut} ${affaire.action}`,
    });
  }

  return documents;
}

/** Repli français : minuscules sans accents, comme le ferait `unaccent`. */
function normaliser(texte) {
  return texte
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

// Mots vides du dictionnaire français de PostgreSQL, réduits à l'utile ici.
const MOTS_VIDES = new Set([
  'le', 'la', 'les', 'un', 'une', 'des', 'du', 'de', 'et', 'ou', 'a', 'au', 'aux',
  'en', 'dans', 'sur', 'pour', 'par', 'avec', 'sans', 'que', 'qui', 'est', 'sont',
  'ce', 'cette', 'ces', 'il', 'elle', 'nous', 'vous', 'ils', 'elles', 'plus',
]);

function jetons(texte) {
  return normaliser(texte)
    .split(/[^a-z0-9]+/)
    .filter((jeton) => jeton.length > 1 && !MOTS_VIDES.has(jeton));
}

/**
 * Approximation locale de la chaîne du pipeline : recherche plein texte
 * française, puis reclassement des meilleurs résultats.
 *
 * Le vrai classement viendra de PostgreSQL (`to_tsvector('french', …)`) puis
 * d'une passe du modèle sur les 50 premiers documents. Ici, tout tient en
 * mémoire sur quelques documents : c'est une maquette du rendu, pas de
 * l'algorithme. Le champ `methode` le dit explicitement à l'utilisateur.
 */
export function rechercher(question, limite = 20) {
  const debut = Date.now();
  const termes = jetons(question ?? '');
  const resultats = [];

  if (termes.length > 0) {
    for (const document of corpus()) {
      const sujet = normaliser(document.sujet);
      const corps = normaliser(document.corps);
      const tout = `${sujet} ${corps}`;

      // Conjonction, comme `websearch_to_tsquery` : tous les termes présents.
      if (!termes.every((terme) => tout.includes(terme))) continue;

      // Rang plein texte : le sujet pèse plus que le corps (poids A contre C).
      let rang = 0;
      for (const terme of termes) {
        rang += (sujet.split(terme).length - 1) * 4;
        rang += (corps.split(terme).length - 1) * 1;
      }

      // Reclassement : la phrase entière retrouvée telle quelle prime sur des
      // termes dispersés — c'est ce que le modèle apporte sur le vrai corpus.
      const phrase = normaliser(question).trim();
      const bonus = phrase && tout.includes(phrase) ? 6 : 0;

      resultats.push({
        cnv_id: document.cnv_id,
        sujet: document.sujet,
        client: document.client,
        date: document.date,
        extrait: extraire(document.corps, termes),
        rang: rang + bonus,
      });
    }
  }

  resultats.sort((a, b) => b.rang - a.rang);
  const meilleur = resultats[0]?.rang ?? 1;

  return {
    question,
    resultats: resultats.slice(0, limite).map(({ rang, ...reste }) => ({
      ...reste,
      pertinence: Math.round((rang / meilleur) * 100) / 100,
    })),
    methode: 'plein_texte_fr + reclassement (démonstration locale)',
    duree_ms: Math.max(1, Date.now() - debut),
  };
}

/** Fenêtre de texte autour de la première occurrence, comme `ts_headline`. */
function extraire(corps, termes, largeur = 160) {
  const normalise = normaliser(corps);
  let position = -1;
  for (const terme of termes) {
    const trouve = normalise.indexOf(terme);
    if (trouve !== -1 && (position === -1 || trouve < position)) position = trouve;
  }
  if (position === -1) return corps.slice(0, largeur);

  const debut = Math.max(0, position - largeur / 3);
  const extrait = corps.slice(debut, debut + largeur).trim();
  return `${debut > 0 ? '…' : ''}${extrait}${debut + largeur < corps.length ? '…' : ''}`;
}
